/**
 * Detail-Page Template: Coordinated item detail view
 *
 * This template composes a complete item detail page using existing blocks:
 * - Hero section (custom - unique to detail pages)
 * - Stats block (reuses existing stats renderer)
 * - List block (reuses existing list renderer for features)
 * - Interactive block (reuses existing actions renderer)
 * - Gallery (custom - unique to detail pages)
 */

import { registerBlockRenderer, renderBlock } from '../registry';
import { escapeHtml, createBlockWrapper, handleImageError } from '../utils';
import type { DetailPageData } from './types';

// =============================================================================
// DETAIL-PAGE TEMPLATE
// =============================================================================

registerBlockRenderer('detail-page', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const templateData = data as DetailPageData;

  if (!templateData.hero) {
    console.warn('[detail-page] Missing required "hero" field');
    return;
  }

  const wrapper = createBlockWrapper('detail-page');
  wrapper.classList.add('pms-template-detail-page');

  // Hero section (custom - unique to detail pages)
  const heroSection = renderHero(templateData.hero);
  wrapper.appendChild(heroSection);

  // Gallery (if multiple images) - custom section
  if (templateData.gallery && templateData.gallery.length > 1) {
    const gallerySection = document.createElement('div');
    gallerySection.className = 'pms-detail-gallery-wrapper';
    renderBlock(
      { type: 'gallery', data: { images: templateData.gallery.map(url => ({ url })) } },
      gallerySection,
      onAction
    );
    wrapper.appendChild(gallerySection);
  }

  // Stats section - reuse existing stats block
  if (templateData.stats && templateData.stats.length > 0) {
    const statsSection = document.createElement('div');
    statsSection.className = 'pms-detail-stats-wrapper';
    renderBlock(
      { type: 'stats', data: { items: templateData.stats } },
      statsSection,
      onAction
    );
    wrapper.appendChild(statsSection);
  }

  // Features section - reuse existing list block
  if (templateData.features && templateData.features.length > 0) {
    const featuresSection = document.createElement('div');
    featuresSection.className = 'pms-detail-features-wrapper';
    // Convert features to list format (ListItem expects 'primary' and 'secondary')
    const listItems = templateData.features.map(f => ({
      primary: f.title,
      secondary: f.description,
      icon: f.icon || '✓'
    }));
    renderBlock(
      { type: 'list', data: { title: 'Features', items: listItems } },
      featuresSection,
      onAction
    );
    wrapper.appendChild(featuresSection);
  }

  // Actions section - reuse existing interactive block
  if (templateData.actions && templateData.actions.length > 0) {
    const actionsSection = document.createElement('div');
    actionsSection.className = 'pms-detail-actions-wrapper';
    // Convert to interactive actions format
    const actions = templateData.actions.map(a => ({
      label: a.label,
      value: a.url ? `link:${a.url}` : a.action,
      variant: a.variant,
      url: a.url
    }));
    renderBlock(
      { type: 'interactive', data: { type: 'actions', actions } },
      actionsSection,
      onAction
    );
    wrapper.appendChild(actionsSection);
  }

  container.appendChild(wrapper);
});

/**
 * Render the hero section (unique to detail pages)
 */
function renderHero(hero: DetailPageData['hero']): HTMLElement {
  const section = document.createElement('div');
  section.className = 'pms-hero'; // Reuse existing hero styles

  let html = '';

  // Image
  if (hero.image) {
    html += `
      <div class="pms-hero-image-wrapper">
        <img class="pms-hero-image" src="${escapeHtml(hero.image)}" alt="${escapeHtml(hero.title)}" loading="lazy" />
      </div>
    `;
  }

  // Content
  html += '<div class="pms-hero-content">';

  if (hero.badge) {
    html += `<span class="pms-hero-badge">${escapeHtml(hero.badge)}</span>`;
  }

  html += `<h2 class="pms-hero-title">${escapeHtml(hero.title)}</h2>`;

  if (hero.subtitle) {
    html += `<p class="pms-hero-subtitle">${escapeHtml(hero.subtitle)}</p>`;
  }

  if (hero.price) {
    html += `<div class="pms-hero-price">${escapeHtml(hero.price)}</div>`;
  }

  html += '</div>';

  section.innerHTML = html;

  // Handle image error
  const img = section.querySelector('.pms-hero-image') as HTMLImageElement;
  if (img) {
    handleImageError(img, false);
  }

  return section;
}
